import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '@auction/shared';
import { api } from '../lib/api';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, name: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  setDemoAuth: (token: string, user: AuthUser) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await api.post<{ success: boolean; data: { user: AuthUser; token: string } }>(
          '/api/auth/login',
          { email, password }
        );
        set({
          user: response.data.user,
          token: response.data.token,
          isAuthenticated: true,
        });
      },

      register: async (email: string, name: string, password: string) => {
        const response = await api.post<{ success: boolean; data: { user: AuthUser; token: string } }>(
          '/api/auth/register',
          { email, name, password }
        );
        set({
          user: response.data.user,
          token: response.data.token,
          isAuthenticated: true,
        });
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      refreshToken: async () => {
        try {
          const response = await api.post<{ token: string }>('/api/auth/refresh');
          set({ token: response.token });
        } catch {
          get().logout();
        }
      },

      setDemoAuth: (token: string, user: AuthUser) => {
        set({
          user,
          token,
          isAuthenticated: true,
        });
      },
    }),
    {
      name: 'auction-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
